/**
 * Mirrors the US PFC session's usPfcDepositPath decision. Booking timestamps
 * are stored as wall-clock UTC by combineDateAndTime on the API; do not use
 * browser-local midnight or a date-only timestamp for this boundary.
 */
export type DepositCopyVariant = "generic" | "near_request" | "near_instant" | "far_request" | "far_instant";

export function checkoutDepositCopyVariant({
  countryCode, rentalType, deposit, dateFrom, pickupTime, isInstant, paymentTiming, nowMs = Date.now(),
}: {
  countryCode: string | null | undefined;
  rentalType: string;
  deposit: number;
  dateFrom: string;
  pickupTime: string | null | undefined;
  isInstant: boolean;
  paymentTiming: "pay_now" | "pay_later";
  nowMs?: number;
}): DepositCopyVariant | null {
  if (!(deposit > 0)) return null;
  if (countryCode?.toUpperCase() !== "US" || rentalType !== "driving" || paymentTiming === "pay_later") return "generic";
  const match = pickupTime?.trim().match(/^(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(am|pm)?$/i);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateFrom) || !match) return "generic";
  let hour = Number(match[1]);
  const minute = Number(match[2] ?? 0);
  const second = Number(match[3] ?? 0);
  const period = match[4]?.toLowerCase();
  if (minute > 59 || second > 59 || (period ? hour < 1 || hour > 12 : hour > 23)) return "generic";
  if (period) hour = (hour % 12) + (period === "pm" ? 12 : 0);
  const pickupMs = Date.parse(`${dateFrom}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}:${String(second).padStart(2, "0")}.000Z`);
  if (!Number.isFinite(pickupMs)) return "generic";
  const near = pickupMs - nowMs <= 72 * 60 * 60 * 1000;
  return `${near ? "near" : "far"}_${isInstant ? "instant" : "request"}`;
}

export function depositCopyKey(variant: DepositCopyVariant, surface: "note" | "explainer"): string {
  return `checkout.deposit_timing.${variant}_${surface}`;
}